import React, { useState, useEffect, useMemo } from 'react'
import { fetchAllUsers, createUser, updateUser, deleteUser, resetUserPassword } from '../services/authService'
import { useToast } from './ToastProvider'

export default function AdminUserManagement() {
  const toast = useToast()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [showResetModal, setShowResetModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [newPassword, setNewPassword] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee',
    department: '',
    designation: '',
    employee_id: '',
    contact: ''
  })

  const emptyForm = {
    name: '',
    email: '',
    password: '',
    role: 'employee',
    department: '',
    designation: '',
    employee_id: '',
    contact: ''
  }

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const data = await fetchAllUsers()
      setUsers(data)
    } catch (err) {
      toast.error('Failed to load users')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // Search/filter logic
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users
    const q = searchQuery.toLowerCase().trim()
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.employee_id || '').toLowerCase().includes(q) ||
      (u.department || '').toLowerCase().includes(q) ||
      (u.designation || '').toLowerCase().includes(q)
    )
  }, [users, searchQuery])

  const handleCreateUser = async (e) => {
    e.preventDefault()
    
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Name, email, and password are required')
      return
    }

    try {
      setLoading(true)
      const result = await createUser(formData)
      setUsers([...users, result.user])
      toast.success(`User ${formData.name} created successfully`)
      setFormData({ ...emptyForm })
      setShowForm(false)
    } catch (err) {
      toast.error(err.message || 'Failed to create user')
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }

    try {
      setLoading(true)
      await resetUserPassword(selectedUser.id, newPassword)
      toast.success(`Password reset for ${selectedUser.name}`)
      setShowResetModal(false)
      setSelectedUser(null)
      setNewPassword('')
      loadUsers()
    } catch (err) {
      toast.error(err.message || 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return
    }

    try {
      setLoading(true)
      await deleteUser(userId)
      setUsers(users.filter(u => u.id !== userId))
      toast.success('User deleted successfully')
    } catch (err) {
      toast.error(err.message || 'Failed to delete user')
    } finally {
      setLoading(false)
    }
  }

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      employee: 'bg-blue-100 text-blue-800',
      security: 'bg-yellow-100 text-yellow-800'
    }
    return colors[role] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">User Management</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="rounded-lg bg-saffron-600 px-4 py-2 text-sm font-medium text-white hover:bg-saffron-700"
        >
          {showForm ? 'Cancel' : '+ Add User'}
        </button>
      </div>

      {showForm && (
        <div className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-saffron-200">
          <h4 className="mb-4 font-medium text-gray-900">Create New User</h4>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Full name"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@igus.net"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Minimum 6 characters"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                >
                  <option value="admin">Admin</option>
                  <option value="employee">Employee</option>
                  <option value="security">Security</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                <input
                  type="text"
                  value={formData.employee_id}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  placeholder="e.g., 10001"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                <input
                  type="text"
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  placeholder="e.g., Product Manager"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="e.g., Operations"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact</label>
                <input
                  type="tel"
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  placeholder="+91-XXXXXXXXXX"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-saffron-600 px-4 py-2 text-sm font-medium text-white hover:bg-saffron-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, email, employee ID, department, or designation..."
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 pl-10 text-sm focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
        />
        <svg className="absolute left-3 top-3 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {searchQuery && (
          <span className="absolute right-3 top-2.5 text-xs text-gray-500">
            {filteredUsers.length} of {users.length} users
          </span>
        )}
      </div>

      {/* Users Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">Name</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">Emp ID</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">Email</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">Role</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">Department</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">Designation</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">Contact</th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredUsers.map(user => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-3 py-3 text-sm text-gray-900 font-medium">{user.name}</td>
                <td className="px-3 py-3 text-sm text-gray-600 font-mono">{user.employee_id || '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-600">{user.email}</td>
                <td className="px-3 py-3">
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                    {user.role}
                  </span>
                </td>
                <td className="px-3 py-3 text-sm text-gray-600">{user.department || '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-600">{user.designation || '-'}</td>
                <td className="px-3 py-3 text-sm text-gray-600">
                  {user.contact || <span className="text-gray-400 italic">Not provided</span>}
                </td>
                <td className="px-3 py-3 text-sm space-x-2">
                  <button
                    onClick={() => {
                      setSelectedUser(user)
                      setShowResetModal(true)
                    }}
                    className="rounded border border-blue-300 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
                  >
                    Reset Password
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    className="rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan="8" className="px-4 py-8 text-center text-sm text-gray-500">
                  {searchQuery ? 'No users match your search.' : 'No users found.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Reset Password Modal */}
      {showResetModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="rounded-lg bg-white p-6 shadow-lg w-full max-w-sm mx-4">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Reset Password</h3>
            <p className="mb-4 text-sm text-gray-600">
              Set a new password for <strong>{selectedUser.name}</strong>
              {selectedUser.employee_id && (
                <span className="text-gray-400"> (ID: {selectedUser.employee_id})</span>
              )}
            </p>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (minimum 6 characters)"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm mb-4 focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
            />
            <div className="flex gap-2">
              <button
                onClick={handleResetPassword}
                disabled={loading || newPassword.length < 6}
                className="flex-1 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Resetting...' : 'Reset'}
              </button>
              <button
                onClick={() => {
                  setShowResetModal(false)
                  setSelectedUser(null)
                  setNewPassword('')
                }}
                className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
